import { useMemo } from 'react'
import type { Availability, AvailabilityPoint } from '../api/types'
import { formatDuration } from '../theme/charts'

/**
 * Availability over time, one bar per hour.
 *
 * <h3>How the number is produced</h3>
 * Every probe writes a row recording whether it succeeded. Those rows are
 * rolled up hourly into `total_runs` and `ok_runs`; one bar is one hour, and
 * the headline figure is `sum(ok) / sum(total)` across the range.
 *
 * <h3>Why the bands are not percentages</h3>
 * A success ratio is not comparable between targets. At a 30s interval an hour
 * holds 120 probes, so a single failure is 99.2%; at 60s it holds 60, and the
 * same failure is 98.3%. Fixed percentage thresholds would put an identical
 * blip in different colours on different targets. So a bar is scored by
 * *estimated downtime* — failed probes × the poll interval — against two
 * boundaries that both mean something: the point the alert would have fired,
 * and half the hour.
 *
 * <h3>Why bar height, not just colour</h3>
 * The two most important bands are "fine" and "failing", and green vs red is
 * exactly the pair that deuteranopia collapses — measured ΔE 3.5, far below
 * any usable floor. Height carries the same information independently of
 * colour: the filled portion is that hour's uptime, so a failing hour is a
 * stub and a healthy one is a full bar whatever you can see.
 */

type Band = 'ok' | 'blip' | 'alerted' | 'failing' | 'nodata'

const FILL: Record<Band, string> = {
  ok: 'var(--status-ok-fill)',
  blip: 'var(--status-ok-deep)',
  alerted: 'var(--status-warning-fill)',
  failing: 'var(--status-critical-fill)',
  nodata: 'var(--status-unknown-fill)',
}

/** A fully-down hour must still be visible, not an empty gap. */
const MIN_FILL_PCT = 12

export function UptimeStrip({ availability }: { availability: Availability }) {
  const points = availability.points
  const interval = availability.intervalSeconds || 30
  const alertAfter = availability.unreachableAfterSeconds || 120

  const bandOf = (p: AvailabilityPoint): Band => {
    if (p.total === 0) return 'nodata'
    const failed = p.total - p.ok
    if (failed === 0) return 'ok'
    const downtime = failed * interval
    if (downtime < alertAfter) return 'blip'
    // Half the hour unreachable: more down than up, a sustained outage rather
    // than an incident inside an otherwise working hour.
    return p.ratio <= 0.5 ? 'failing' : 'alerted'
  }

  const ticks = useMemo(() => buildTicks(points), [points])

  if (points.length === 0) {
    return <div className="banner empty">No probe history for this range yet.</div>
  }

  const failed = points.reduce((n, p) => n + (p.total - p.ok), 0)
  const probes = points.reduce((n, p) => n + p.total, 0)
  const downtime = failed * interval

  return (
    <div>
      <div className="spread" style={{ marginBottom: 'var(--space-3)' }}>
        <div className="row" style={{ gap: 'var(--space-4)', alignItems: 'baseline' }}>
          <span className="stat-value" style={{ fontSize: 'var(--text-xl)' }}>
            {(availability.uptimeRatio * 100).toFixed(2)}%
          </span>
          <span className="muted" style={{ fontSize: 'var(--text-xs)' }}>
            {failed === 0
              ? `${probes} probes, none failed`
              : `${failed} of ${probes} probes failed · ≈ ${formatDuration(downtime)} unreachable`}
          </span>
        </div>
        <span className="muted" style={{ fontSize: 'var(--text-xs)' }}>
          {points.length}h · probed every {interval}s
        </span>
      </div>

      <div className="uptime-strip" role="img"
           aria-label={`Availability ${(availability.uptimeRatio * 100).toFixed(2)} percent over ${points.length} hours`}>
        {points.map((p, i) => {
          const band = bandOf(p)
          const nFailed = p.total - p.ok
          const est = nFailed * interval
          const d = new Date(p.t)
          // A rule at every midnight, so a week of bars is readable as days
          // rather than as one long run of cells.
          const dayStart = i > 0 && d.getHours() === 0
          const height = p.total === 0
            ? 100
            : Math.max(MIN_FILL_PCT, p.ratio * 100)

          return (
            <div
              key={p.t}
              className={`uptime-cell${dayStart ? ' is-daystart' : ''}`}
              title={
                `${d.toLocaleString()}\n` +
                (p.total === 0
                  ? 'No probes recorded in this hour'
                  : `${p.ok}/${p.total} probes OK (${(p.ratio * 100).toFixed(1)}%)` +
                    (nFailed > 0 ? `\n${nFailed} failed ≈ ${formatDuration(est)} unreachable` : '') +
                    (p.avgDurationMs ? `\nmean response ${Math.round(p.avgDurationMs)} ms` : ''))
              }
            >
              <span
                className={`uptime-fill is-${band}`}
                style={{ height: `${height}%`, background: FILL[band] }}
              />
            </div>
          )
        })}
      </div>

      {/* Time axis. Sparse ticks positioned proportionally rather than one
          label per bar, which at a week's 168 bars would be unreadable. */}
      <div className="uptime-axis">
        {ticks.map((tick) => (
          <span
            key={tick.index}
            className="uptime-tick"
            style={{ left: `${tick.pct}%` }}
          >
            {tick.label}
          </span>
        ))}
      </div>

      <div className="chart-legend" style={{ marginTop: 'var(--space-3)' }}>
        <LegendItem colour={FILL.ok} height={100} label="Fully up" />
        <LegendItem colour={FILL.blip} height={92}
                    label={`Brief failure, under ${formatDuration(alertAfter)}`} />
        <LegendItem colour={FILL.alerted} height={62}
                    label={`${formatDuration(alertAfter)}+ — would have alerted`} />
        <LegendItem colour={FILL.failing} height={22} label="Down for most of the hour" />
        <LegendItem colour={FILL.nodata} height={100} label="No probes recorded" />
      </div>

      <p className="muted" style={{ fontSize: 'var(--text-xs)', marginTop: 'var(--space-2)' }}>
        Bar height is that hour's uptime; colour is the band. Bands are estimated
        downtime — failed probes × the {interval}s poll interval — not a fixed
        percentage, so they mean the same thing on targets polled at different
        rates. The amber boundary is the <code>{formatDuration(alertAfter)}</code>{' '}
        window on the <code>target-unreachable</code> rule; red is more than half
        the hour. Hours with no probes are excluded from the headline figure: a
        collector outage is not evidence about the target.
      </p>
    </div>
  )
}

function LegendItem({ colour, height, label }: {
  colour: string
  height: number
  label: string
}) {
  return (
    <span className="item">
      <span className="legend-bar" aria-hidden="true">
        <span style={{ height: `${height}%`, background: colour }} />
      </span>
      {label}
    </span>
  )
}

interface Tick { index: number; pct: number; label: string }

/**
 * Choose a readable set of axis labels for the span actually shown.
 *
 * <p>Prefers midnight boundaries when the range covers days, because "which
 * day" is the question being asked; falls back to evenly spaced clock times
 * for shorter spans.
 */
function buildTicks(points: AvailabilityPoint[]): Tick[] {
  if (points.length < 2) return []

  const n = points.length
  const first = new Date(points[0].t)
  const last = new Date(points[n - 1].t)
  const spanHours = (last.getTime() - first.getTime()) / 3_600_000
  const pctOf = (i: number) => (i / (n - 1)) * 100

  // Roughly one label per 110px of a typical card; capped so labels never
  // collide on a narrow screen.
  const maxTicks = 8

  if (spanHours <= 36) {
    // Inside a day or two: clock time is what identifies the problem window.
    const step = Math.max(1, Math.ceil(n / maxTicks))
    const out: Tick[] = []
    for (let i = 0; i < n; i += step) {
      const d = new Date(points[i].t)
      out.push({
        index: i,
        pct: pctOf(i),
        label: d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
      })
    }
    return out
  }

  // Longer spans: label the day boundaries, thinned to fit.
  const midnights: number[] = []
  for (let i = 0; i < n; i++) {
    if (new Date(points[i].t).getHours() === 0) midnights.push(i)
  }
  const source = midnights.length >= 2 ? midnights : [...Array(n).keys()]
  const step = Math.max(1, Math.ceil(source.length / maxTicks))

  const out: Tick[] = []
  for (let k = 0; k < source.length; k += step) {
    const i = source[k]
    const d = new Date(points[i].t)
    out.push({
      index: i,
      pct: pctOf(i),
      label: d.toLocaleDateString([], { day: '2-digit', month: 'short' }),
    })
  }
  return out
}
